// src/components/frontpage/SuggestListingModal.jsx
// Modal for submitting a new directory listing suggestion or a correction to an existing one.
// Follows the ConfirmationModal/AuthRequiredModal pattern (fixed overlay, controlled via props).

import React, { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { submitSuggestion } from '../../data/directoryService';
import TagInput from '../shared/TagInput';
import Modal from '../core/Modal';

const SECTION_OPTIONS = [
  { value: 'official_contact', labelKey: 'section_official_contact' },
  { value: 'utility', labelKey: 'section_utility' },
  { value: 'directory', labelKey: 'section_directory' }
];

const CATEGORY_OPTIONS = {
  directory: [
    { value: 'health', labelKey: 'dir_health' },
    { value: 'home', labelKey: 'dir_home' },
    { value: 'official', labelKey: 'dir_official' },
    { value: 'learning', labelKey: 'dir_learning' },
    { value: 'food_shopping', labelKey: 'dir_food_shopping' },
    { value: 'activities', labelKey: 'dir_activities' },
    { value: 'transport', labelKey: 'dir_transport' },
    { value: 'eating_drinking', labelKey: 'dir_eating_drinking' }
  ],
  utility: [
    { value: 'electricity', labelKey: 'utility_electricity' },
    { value: 'water', labelKey: 'utility_water' },
    { value: 'gas', labelKey: 'utility_gas' },
    { value: 'internet_phone', labelKey: 'utility_internet' }
  ],
  official_contact: [
    { value: 'junta', labelKey: 'contact_junta' },
    { value: 'camara', labelKey: 'contact_camara' },
    { value: 'health_centre', labelKey: 'contact_health_centre' }
  ]
};

const SuggestListingModal = ({
  boundaryId,
  listing = null,
  onClose,
  onSubmitted,
  canReviewSuggestions = false,
  // Pre-select from the directory column's open section/category when opening the form to
  // suggest a NEW listing (column shell brief, Scope 2). Ignored for a correction — `listing`'s
  // own section/category always wins there, since a correction is about a different, existing
  // entry, not whatever happened to be open when the CTA was clicked. Both stay user-changeable;
  // this only seeds the initial value.
  initialSection = null,
  initialCategory = null
}) => {
  const { t } = useTranslation('frontpage');
  const theme = useTheme();
  const isCorrection = Boolean(listing);
  // The Section picker is always shown for a NEW listing. On a CORRECTION it is shown only to a
  // reviewer (Steward-for-boundary or admin), who may re-file a mis-sectioned entry — the B1a
  // migration makes a changed section persist on approval. Non-reviewers keep the section hidden
  // (the correction inherits the listing's existing section, unchanged behaviour).
  const showSectionPicker = !isCorrection || canReviewSuggestions;

  const [form, setForm] = useState({
    section: listing?.section || initialSection || 'directory',
    category: listing?.category || initialCategory || '',
    name: listing?.name || '',
    phone: listing?.phone || '',
    email: listing?.email || '',
    website: listing?.website || '',
    address: listing?.address || '',
    description: listing?.description || ''
  });
  const [tags, setTags] = useState(listing?.tags || []);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError(t('suggest_name_required'));
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload = { ...form };
    // Strip empty strings
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') delete payload[k];
    });
    // Include tags only when non-empty
    if (tags.length > 0) {
      payload.tags = tags;
    }

    const result = await submitSuggestion({
      boundaryId,
      listingId: listing?.id || null,
      payload,
      note: note.trim() || null
    });

    setSubmitting(false);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => onSubmitted(), 1500);
    } else {
      setError(result.error?.message || t('suggest_error'));
    }
  };

  const categories = CATEGORY_OPTIONS[form.section] || [];

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '13px',
    color: '#1f2937',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '3px',
    display: 'block'
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      panelStyle={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '420px',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        fontFamily: 'OpenSauceSans, sans-serif'
      }}
    >
        {/* Title */}
        <h2 style={{
          fontSize: '16px',
          fontWeight: '700',
          color: theme.primary,
          marginTop: 0,
          marginBottom: '16px'
        }}>
          {isCorrection ? t('suggest_correction_title') : t('suggest_listing_title')}
        </h2>

        {success ? (
          <div style={{
            textAlign: 'center',
            padding: '24px 0',
            color: '#059669',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {t('suggest_success')}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Section — always for a new listing; on a correction only for a reviewer (re-filing) */}
            {showSectionPicker && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>{t('suggest_section')}</label>
                <select
                  value={form.section}
                  onChange={(e) => {
                    handleChange('section', e.target.value);
                    handleChange('category', '');
                  }}
                  style={inputStyle}
                >
                  {SECTION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Category */}
            {categories.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>{t('suggest_category')}</label>
                <select
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  style={inputStyle}
                >
                  <option value="">{t('suggest_select_category')}</option>
                  {categories.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Name */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('suggest_name')} *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                style={inputStyle}
                placeholder={t('suggest_name_placeholder')}
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('suggest_phone')}</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('suggest_email')}</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Website */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('suggest_website')}</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Address */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('suggest_address')}</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Tags (directory section only) */}
            {form.section === 'directory' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>{t('suggest_tags')}</label>
                <TagInput
                  tags={tags}
                  onAdd={(newTags) => setTags(prev => [...prev, ...newTags])}
                  onRemove={(tag) => setTags(prev => prev.filter(t2 => t2 !== tag))}
                  max={5}
                  caseMode="lower"
                  singleWord={true}
                  placeholder={t('suggest_tags_placeholder')}
                  addLabel={t('suggest_tags_add')}
                />
              </div>
            )}

            {/* Note to reviewer */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('suggest_note')}</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder={t('suggest_note_placeholder')}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                marginBottom: '12px',
                padding: '8px 10px',
                background: '#fef2f2',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#dc2626'
              }}>
                {error}
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  fontFamily: 'OpenSauceSans, sans-serif',
                  fontSize: '13px',
                  color: '#6b7280',
                  cursor: 'pointer'
                }}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: submitting ? '#93c5fd' : theme.primary,
                  fontFamily: 'OpenSauceSans, sans-serif',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: submitting ? 'default' : 'pointer'
                }}
              >
                {submitting ? t('suggest_submitting') : t('suggest_submit')}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
};

export default SuggestListingModal;
